import type { AdminAssignmentStatus } from '../api/adminConsole';

// RBAC Phase 2 assignment expiry. Mirrors admin-api: `expired` once `expiresAt <= now`, `expiring`
// when it expires within 7 days, `active` otherwise. An expired assignment never grants and is
// never counted.

const DAY_MS = 24 * 60 * 60 * 1000;
export const EXPIRING_WINDOW_MS = 7 * DAY_MS;

function parseMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/** Status for an expiry timestamp at `now`. No expiry is always `active`. */
export function assignmentStatusAt(
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): AdminAssignmentStatus {
  const ms = parseMs(expiresAt);
  if (ms == null) return 'active';
  if (ms <= now) return 'expired';
  if (ms - now <= EXPIRING_WINDOW_MS) return 'expiring';
  return 'active';
}

/**
 * The status to show. An expiry that has passed on this clock wins over a stale server status;
 * otherwise the server's value is used, falling back to the computed one.
 */
export function effectiveAssignmentStatus(
  assignment: { status?: AdminAssignmentStatus | null; expiresAt?: string | null },
  now: number = Date.now(),
): AdminAssignmentStatus {
  const computed = assignmentStatusAt(assignment.expiresAt, now);
  if (computed === 'expired') return 'expired';
  return assignment.status ?? computed;
}

/** Expired assignments grant nothing and are not counted. */
export function isAssignmentCounted(
  assignment: { status?: AdminAssignmentStatus | null; expiresAt?: string | null },
  now: number = Date.now(),
): boolean {
  return effectiveAssignmentStatus(assignment, now) !== 'expired';
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/** Short relative expiry label, e.g. "No expiry", "Expires in 3 days", "Expired 2 days ago". */
export function formatAssignmentExpiry(
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): string {
  const ms = parseMs(expiresAt);
  if (ms == null) return 'No expiry';
  const diff = ms - now;
  if (diff <= 0) {
    const days = Math.floor(-diff / DAY_MS);
    if (days === 0) return 'Expired today';
    return `Expired ${plural(days, 'day')} ago`;
  }
  const hours = Math.ceil(diff / (60 * 60 * 1000));
  if (hours < 24) return `Expires in ${plural(hours, 'hour')}`;
  return `Expires in ${plural(Math.ceil(diff / DAY_MS), 'day')}`;
}

export type StatusChipTone = 'default' | 'success' | 'warning' | 'error';

/** Chip label + colour for one assignment status. */
export function assignmentStatusChip(status: AdminAssignmentStatus): { label: string; color: StatusChipTone } {
  switch (status) {
    case 'expired':
      return { label: 'Expired', color: 'error' };
    case 'expiring':
      return { label: 'Expiring', color: 'warning' };
    default:
      return { label: 'Active', color: 'success' };
  }
}

/** Badge for a user's worst assignment status; null for `none`/missing (nothing to flag). */
export function userAssignmentBadge(
  status: 'none' | AdminAssignmentStatus | null | undefined,
): { label: string; color: StatusChipTone } | null {
  if (!status || status === 'none') return null;
  if (status === 'active') return { label: 'Active', color: 'success' };
  return assignmentStatusChip(status);
}

/** `YYYY-MM-DD` (a date input) → ISO at the end of that local day; undefined when blank/invalid. */
export function expiryDateInputToIso(value: string | null | undefined): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
  if (Number.isNaN(d.getTime()) || d.getMonth() !== Number(m[2]) - 1) return undefined;
  return d.toISOString();
}

/** ISO → `YYYY-MM-DD` in local time, for a date input; '' when absent. */
export function isoToExpiryDateInput(iso: string | null | undefined): string {
  const ms = parseMs(iso);
  if (ms == null) return '';
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** New expiry `days` after the later of now and the current expiry (an extension never shortens). */
export function extendedExpiry(
  currentExpiresAt: string | null | undefined,
  days: number,
  now: number = Date.now(),
): string {
  const current = parseMs(currentExpiresAt);
  const base = current != null && current > now ? current : now;
  return new Date(base + days * DAY_MS).toISOString();
}

/** Readable absolute timestamp; '—' when absent. */
export function formatIamTimestamp(iso: string | null | undefined): string {
  const ms = parseMs(iso);
  if (ms == null) return '—';
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
